import {
  type AnalysisConflict,
  type AnalysisConflictDecider,
  type AnalysisLevel,
  type AnalysisShares,
  isLevelFixed,
} from '../schemas/analysis.js';
import {
  ANALYST_FUNCTIONS,
  ANALYST_LEVELS,
  type AnalystFunction,
  type AnalystLevel,
} from '../schemas/analyst.js';
import { NOYAU_CODES, type NoyauCode } from '../schemas/noyau.js';

/**
 * THE CODE'S HALF of the analyst (analyste.md §6, issue #196): what the code
 * makes of the parts the first pass gave - the levels it fixes, the conflicts it
 * arbitrates, the noyaux it eliminates, the compatibility of the six.
 *
 * Pure, and in `shared` like every rule both a server and a reader may have to
 * replay: the server runs it between the two passes, the parcours explains it,
 * and the tests of this repository hold it to the example of §2 value by value.
 * Nothing here calls a model, draws, or reads a clock.
 *
 * The model gives PARTS - three whole numbers summing to 100 for each objective
 * that carries a level - and never knows which level an objective carries
 * (G10): fixing a level, choosing between two that disagree and striking out the
 * noyaux that cannot be are the code's, so the combinatorics of the triplets and
 * the confirmation bias stay out of the model (G2, G3).
 */

/** The three functions a noyau carries at the three levels, read off its triplet. */
const TRIPLET_FUNCTIONS: ReadonlyMap<NoyauCode, readonly AnalystFunction[]> = new Map(
  NOYAU_CODES.map((code) => [
    code,
    [...code].map((letter) => {
      const fn = ANALYST_FUNCTIONS.find((candidate) => candidate === letter);
      if (fn === undefined) throw new Error(`Not a function of the method: ${letter}`);
      return fn;
    }),
  ]),
);

/**
 * The function a noyau carries at a level (noyau.md §2.1): the letter of its
 * triplet at that level's place - the first at the Pourquoi, the second at the
 * Comment, the third at the Quoi. « GPS » carries G at the Pourquoi, P at the
 * Comment, S at the Quoi.
 */
export function noyauFunctionAt(noyau: NoyauCode, level: AnalystLevel): AnalystFunction {
  const fn = TRIPLET_FUNCTIONS.get(noyau)?.[ANALYST_LEVELS.indexOf(level)];
  if (fn === undefined) throw new Error(`No function at ${level} for ${noyau}`);
  return fn;
}

/* --------------------------------------------------------------------- input */

/**
 * One objective the first pass READ (§6.1): served - it had a live extract - and
 * noted. `level` is the one the frozen protocol gives it, known to the code and
 * never to the pass; `shares` is what the pass answered.
 */
export type LevelledObjective = {
  readonly objectiveId: string;
  readonly name: string;
  readonly level: AnalystLevel;
  readonly shares: AnalysisShares;
};

/** The two certainty thresholds of the section (§3.2). A part « atteint » a threshold at equality. */
export type LevelThresholds = {
  readonly pairThreshold: number;
  readonly soloThreshold: number;
};

/* ----------------------------------------------------------------- fixation */

/** The mean of a function over objectives, to two decimals - what the parcours shows beside the parts. */
function meanOf(objectives: readonly LevelledObjective[], fn: AnalystFunction): number {
  const sum = objectives.reduce((total, objective) => total + objective.shares[fn], 0);
  return Math.round((sum / objectives.length) * 100) / 100;
}

/**
 * The function EVERY objective gives at least `threshold` to, or null.
 *
 * With a threshold above 50 - the bound of the section (K2) - at most one
 * function can qualify, since a part above 50 leaves less than 50 to each of the
 * other two. Looser than that, which no stored section is, the function with the
 * highest smallest part wins, then the order S, G, P: deterministic, and never a
 * reason to throw.
 */
function agreedFunction(
  objectives: readonly LevelledObjective[],
  threshold: number,
): AnalystFunction | null {
  let best: { fn: AnalystFunction; floor: number } | null = null;
  for (const fn of ANALYST_FUNCTIONS) {
    const floor = Math.min(...objectives.map((objective) => objective.shares[fn]));
    if (floor < threshold) continue;
    if (best === null || floor > best.floor) best = { fn, floor };
  }
  return best === null ? null : best.fn;
}

/**
 * FIX the three levels (§6.2, G6, G7, K3), on the objectives the first pass read:
 *
 * | Readable objectives | The level is fixed on a function when | State |
 * |---|---|---|
 * | none | never | `nothing-to-read` (K26) |
 * | one | its part of that function reaches the threshold of an objective alone | `fixed-solo` |
 * | two or more | EACH gives at least the threshold of a pair to THE SAME function | `fixed-pair` |
 * | otherwise | - | `not-fixed` |
 *
 * « Atteindre » counts the equality. Two objectives, one giving 74 to the
 * Souverain and the other 65, fix nothing: the symmetry makes the certainty, not
 * the frankest objective. Three readable objectives or more fix a level when all
 * of them agree (K3).
 *
 * The conflicts are NOT arbitrated here (`arbitrateLevels`): the three levels
 * come back as they stand, in the order of a triplet, two of them possibly
 * fixed on the same function.
 */
export function fixLevels(
  objectives: readonly LevelledObjective[],
  thresholds: LevelThresholds,
): AnalysisLevel[] {
  return ANALYST_LEVELS.map((level): AnalysisLevel => {
    const readable = objectives.filter((objective) => objective.level === level);
    if (readable.length === 0) {
      return {
        level,
        state: 'nothing-to-read',
        function: null,
        threshold: null,
        decisive: [],
        means: null,
      };
    }
    const alone = readable.length === 1;
    const threshold = alone ? thresholds.soloThreshold : thresholds.pairThreshold;
    const means = {
      S: meanOf(readable, 'S'),
      G: meanOf(readable, 'G'),
      P: meanOf(readable, 'P'),
    };
    const fn = agreedFunction(readable, threshold);
    if (fn === null) {
      return { level, state: 'not-fixed', function: null, threshold, decisive: [], means };
    }
    return {
      level,
      state: alone ? 'fixed-solo' : 'fixed-pair',
      function: fn,
      threshold,
      decisive: readable.map((objective) => ({
        objectiveId: objective.objectiveId,
        share: objective.shares[fn],
      })),
      means,
    };
  });
}

/* ---------------------------------------------------------------- conflicts */

/** The smallest of the parts that decided a level: the floor K4 compares. */
function floorOf(level: AnalysisLevel): number {
  return Math.min(...level.decisive.map((part) => part.share));
}

/**
 * The surer of two fixed levels, and the rule of K4 that said so, tried in this
 * order: a level fixed in SYMMETRY beats an objective alone; then the HIGHEST of
 * the smallest parts of the function among a level's objectives; then the order
 * Pourquoi, Comment, Quoi. Negative when `one` is surer.
 */
function surer(
  one: AnalysisLevel,
  other: AnalysisLevel,
): { order: number; decidedBy: AnalysisConflictDecider } {
  const pair = (level: AnalysisLevel): number => (level.state === 'fixed-pair' ? 0 : 1);
  if (pair(one) !== pair(other)) {
    return { order: pair(one) - pair(other), decidedBy: 'symmetry' };
  }
  if (floorOf(one) !== floorOf(other)) {
    return { order: floorOf(other) - floorOf(one), decidedBy: 'floor' };
  }
  return {
    order: ANALYST_LEVELS.indexOf(one.level) - ANALYST_LEVELS.indexOf(other.level),
    decidedBy: 'order',
  };
}

/**
 * ARBITRATE the conflicts (§6.3, G9, K4): three levels carry three DIFFERENT
 * functions, so two levels - or three - fixed on the same function are a
 * conflict. The code keeps the surest and sets the others aside: they come back
 * `dropped`, keeping the function and the parts they were fixed on so the parcours
 * can say why, and the conflict is recorded with the rule that decided it.
 *
 * A level that is not fixed takes no part, and a level alone on its function is
 * left as it is. The conflicts come back in the order of the functions S, G, P.
 */
export function arbitrateLevels(levels: readonly AnalysisLevel[]): {
  levels: AnalysisLevel[];
  conflicts: AnalysisConflict[];
} {
  const conflicts: AnalysisConflict[] = [];
  const droppedLevels = new Set<AnalystLevel>();
  for (const fn of ANALYST_FUNCTIONS) {
    const claimants = levels.filter((level) => isLevelFixed(level.state) && level.function === fn);
    const [kept, runnerUp, ...others] = [...claimants].sort(
      (one, other) => surer(one, other).order,
    );
    if (kept === undefined || runnerUp === undefined) continue;
    const dropped = [runnerUp, ...others];
    for (const level of dropped) droppedLevels.add(level.level);
    conflicts.push({
      function: fn,
      kept: kept.level,
      dropped: dropped.map((level) => level.level),
      decidedBy: surer(kept, runnerUp).decidedBy,
    });
  }
  return {
    levels: levels.map((level) =>
      droppedLevels.has(level.level) ? { ...level, state: 'dropped' } : level,
    ),
    conflicts,
  };
}

/* -------------------------------------------------------------- elimination */

/** One noyau the levels struck out: the first fixed level it contradicts, and the function that level is fixed on. */
export type NoyauElimination = {
  readonly noyau: NoyauCode;
  readonly level: AnalystLevel;
  readonly function: AnalystFunction;
};

/**
 * The CANDIDATES and the ELIMINATED (§6.4): a candidate carries, at every fixed
 * level, the function of that level; every other noyau is eliminated, and names
 * the FIRST fixed level it contradicts, in the order Pourquoi, Comment, Quoi (K9).
 * Both come back in the order of the table of noyau.md §2.1.
 *
 * After `arbitrateLevels` the fixed levels carry different functions, so there
 * is ALWAYS at least one candidate: six with no level fixed, two with one, one
 * with two or three. Called on levels that were not arbitrated, it may find none.
 */
export function eliminateNoyaux(levels: readonly AnalysisLevel[]): {
  candidates: NoyauCode[];
  eliminated: NoyauElimination[];
} {
  const fixed = levels.filter((level) => isLevelFixed(level.state));
  const candidates: NoyauCode[] = [];
  const eliminated: NoyauElimination[] = [];
  for (const noyau of NOYAU_CODES) {
    const contradicted = fixed.find(
      (level) => level.function !== null && noyauFunctionAt(noyau, level.level) !== level.function,
    );
    if (contradicted === undefined || contradicted.function === null) {
      candidates.push(noyau);
    } else {
      eliminated.push({ noyau, level: contradicted.level, function: contradicted.function });
    }
  }
  return { candidates, eliminated };
}

/* ------------------------------------------------------------ compatibility */

/**
 * The COMPATIBILITY of each of the six noyaux (§6.5, K5): the mean, over the
 * levels that have something to read, of the part the first pass gave the
 * function that noyau places at that level - itself averaged over the readable
 * objectives of the level. A whole number from 0 to 100, rounded to the nearest,
 * a half upwards. Computed for the six, candidates or not, in the order of the
 * table; 0 when no level has anything to read, which the engine never asks (a
 * determination with no readable objective is `no-extracts`).
 *
 * The rounding is done on EXACT integers - the mean of means is a fraction, and
 * a float could land a hair under a true half - so 34.5 is 35 on every machine,
 * as in the example of §2 (GSP).
 */
export function noyauCompatibilities(
  objectives: readonly LevelledObjective[],
): { noyau: NoyauCode; compatibility: number }[] {
  const readable = ANALYST_LEVELS.filter((level) =>
    objectives.some((objective) => objective.level === level),
  );
  return NOYAU_CODES.map((noyau) => {
    if (readable.length === 0) return { noyau, compatibility: 0 };
    const parts = readable.map((level) => {
      const fn = noyauFunctionAt(noyau, level);
      const rows = objectives.filter((objective) => objective.level === level);
      return { sum: rows.reduce((total, row) => total + row.shares[fn], 0), count: rows.length };
    });
    return { noyau, compatibility: roundedMeanOfMeans(parts) };
  });
}

/**
 * The mean of the means `sum / count`, rounded half up, on integers only:
 * with `D` the product of the counts and `N` the sum of `sum * D / count`, the
 * mean is `N / (D * L)` for `L` levels, and its rounding `floor((2N + D*L) / (2*D*L))`.
 */
function roundedMeanOfMeans(parts: readonly { sum: number; count: number }[]): number {
  const common = parts.reduce((product, part) => product * part.count, 1);
  const numerator = parts.reduce((total, part) => total + part.sum * (common / part.count), 0);
  const denominator = common * parts.length;
  return Math.floor((2 * numerator + denominator) / (2 * denominator));
}

/* ------------------------------------------------------------------- whole */

/** What the code reads between the two passes: everything the second pass needs, and the compatibility. */
export type LevelReading = {
  readonly levels: AnalysisLevel[];
  readonly conflicts: AnalysisConflict[];
  readonly candidates: NoyauCode[];
  readonly eliminated: NoyauElimination[];
  readonly compatibility: { noyau: NoyauCode; compatibility: number }[];
};

/**
 * The reading of the levels in one call, the order the engine runs it in (§2):
 * fix, arbitrate, eliminate, and the compatibility of the six. What it hands the
 * second pass is `candidates`, the noyaux to note the style of.
 */
export function readLevels(
  objectives: readonly LevelledObjective[],
  thresholds: LevelThresholds,
): LevelReading {
  const { levels, conflicts } = arbitrateLevels(fixLevels(objectives, thresholds));
  return {
    levels,
    conflicts,
    ...eliminateNoyaux(levels),
    compatibility: noyauCompatibilities(objectives),
  };
}
